import React from "react";
import { MessageCircle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { CONTACT_INFO } from "@/i18n/translations";
import { FOOTER } from "@/constants/testIds/site";

export default function Footer() {
  const { t } = useLanguage();
  const year = new Date().getFullYear();

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer
      data-testid={FOOTER.section}
      className="border-t border-[#1A1A1A]/10 bg-[#1A1A1A] text-[#F7F6F2]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <div className="relative h-16 w-16 overflow-hidden">
                <img src="/logo.png" alt="Decrepalma" className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <span className="font-serif text-3xl tracking-[0.15em] text-white uppercase">
                Decrepalma
              </span>
            </div>
            <p className="mt-5 text-[#F7F6F2]/70 max-w-sm leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-[#F7F6F2]/50 mb-4">
              {t.nav.langLabel === "Idioma"
                ? "Navegar"
                : t.nav.langLabel === "Language"
                ? "Navigate"
                : "Navegar"}
            </p>
            <ul className="space-y-3">
              <li>
                <a
                  href="#services"
                  onClick={scrollTo("services")}
                  className="text-[#F7F6F2]/80 hover:text-[#8B1A1A] transition-colors"
                >
                  {t.footer.links.services}
                </a>
              </li>
              <li>
                <a
                  href="#about"
                  onClick={scrollTo("about")}
                  className="text-[#F7F6F2]/80 hover:text-[#8B1A1A] transition-colors"
                >
                  {t.footer.links.about}
                </a>
              </li>
              <li>
                <a
                  href="#contact"
                  onClick={scrollTo("contact")}
                  className="text-[#F7F6F2]/80 hover:text-[#8B1A1A] transition-colors"
                >
                  {t.footer.links.contact}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.2em] text-[#F7F6F2]/50 mb-4">
              {t.contact.infoTitle}
            </p>
            <ul className="space-y-3 text-[#F7F6F2]/80">
              <li>
                <a href={`mailto:${t.contact.email}`} className="hover:text-[#8B1A1A] transition-colors">
                  {t.contact.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${CONTACT_INFO.phoneRaw}`}
                  className="hover:text-[#8B1A1A] transition-colors"
                >
                  {t.contact.phone}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-whatsapp"
                  href={`https://wa.me/${CONTACT_INFO.whatsappRaw}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 hover:text-[#8B1A1A] transition-colors"
                >
                  <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {t.contact.whatsapp}
                </a>
              </li>
              <li>{t.contact.hours}</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs uppercase tracking-[0.2em] text-[#F7F6F2]/50">
            © {year} Decrepalma. {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
}
